/**
 * Atmosphere state shared by the environment (writer) and the post graph (reader) for one scene (D-047 lighting
 * overhaul): the star direction and colour the key light uses, the baked sky the fog takes its colour from, and the
 * dust patches of the current field. One scattering model: the haze in post/atmosphere.ts is lit by exactly
 * this sun, so fog, dust and god rays never disagree with the shadows.
 *
 * Everything is in render space (camera at the origin, D-011) and mutated in place (allocation-free per frame).
 */
import { Color, Vector3, Vector4, type Scene, type Texture } from 'three/webgpu'

/** Dust patches per field the haze shader evaluates (a structural size, like a pool). */
export const MAX_DUST_PATCHES = 6

export interface AtmosphereState {
  /** Unit direction toward the star (render space). */
  readonly sunDirection: Vector3
  /** Linear key colour × intensity. */
  readonly sunColor: Color
  /** Dust volume: xyz centre (render space), w radius in metres (0 = no dust). */
  readonly dust: Vector4
  /**
   * x optical depth per patch radius at a patch centre, y Henyey-Greenstein anisotropy, z single-scattering albedo,
   * w maximum dust opacity.
   */
  readonly dustParams: Vector4
  /**
   * The dust patches (D-TBD haze): up to MAX_DUST_PATCHES small spheres inside the field's volume, xyz centre (render
   * space), w radius (m; 0 = unused). Written by the environment's setDustVolume, allocation-free.
   */
  readonly patches: readonly Vector4[]
  /** x wisp noise frequency (cells per patch radius), y wisp contrast; z, w reserved. */
  readonly dustShape: Vector4
  /** Linear dust light tint (white mixed toward the system's nebula hues). */
  readonly dustTint: Color
  /** x fog start (m), y density (1/m), z maximum opacity, w sky blur roughness. */
  readonly fog: Vector4
  /** x ambient (sky) share of the dust light, y fog colour gain, z isotropic share of the dust phase; w reserved. */
  readonly tint: Vector4
  /** The PMREM of the baked sky (fog colour), or null before the environment exists. */
  sky: Texture | null
}

const states = new WeakMap<Scene, AtmosphereState>()

export function createAtmosphereState(): AtmosphereState {
  return {
    sunDirection: new Vector3(0, 1, 0),
    sunColor: new Color(1, 1, 1),
    dust: new Vector4(0, 0, 0, 0),
    dustParams: new Vector4(0, 0.6, 0.7, 0),
    patches: Array.from({ length: MAX_DUST_PATCHES }, () => new Vector4(0, 0, 0, 0)),
    dustShape: new Vector4(1, 0, 0, 0),
    dustTint: new Color(1, 1, 1),
    fog: new Vector4(1e9, 0, 0, 0.8),
    tint: new Vector4(0.5, 1, 0, 0),
    sky: null,
  }
}

/** The scene's atmosphere state (created on first use; the environment fills it). */
export function atmosphereOf(scene: Scene): AtmosphereState {
  let s = states.get(scene)
  if (!s) {
    s = createAtmosphereState()
    states.set(scene, s)
  }
  return s
}

/** Deterministic 0..1 sequence (mulberry32) for the patch layout. */
function rng(seed: number): () => number {
  let s = seed >>> 0
  return (): number => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export interface DustPatchShape {
  readonly count: number
  /** Patch radius range, fractions of the field radius. */
  readonly radius: readonly [number, number]
  /** Patch centres scatter up to this fraction of the field radius. */
  readonly spread: number
  readonly seed: number
}

/**
 * The patch layout in field units (xyz offset from the field centre, w radius; both × the field radius), seeded, so
 * every field reads as a few pockets of dust with clear space between them. Built once (not per frame).
 */
export function dustPatchLayout(shape: DustPatchShape): Vector4[] {
  const next = rng(shape.seed)
  const out: Vector4[] = []
  for (let i = 0; i < Math.min(shape.count, MAX_DUST_PATCHES); i++) {
    // Uniform direction, distance biased outward (cube root) so pockets spread through the volume.
    const z = next() * 2 - 1
    const phi = next() * Math.PI * 2
    const ring = Math.sqrt(1 - z * z)
    const d = Math.cbrt(next()) * shape.spread
    const r = shape.radius[0] + (shape.radius[1] - shape.radius[0]) * next()
    out.push(new Vector4(ring * Math.cos(phi) * d, ring * Math.sin(phi) * d, z * d, r))
  }
  return out
}

/** Places the patches around a field centre (render space) at a field radius (0 = no dust). Allocation-free. */
export function placeDustPatches(atmos: AtmosphereState, layout: readonly Vector4[], cx: number, cy: number, cz: number, radius: number): void {
  for (let i = 0; i < atmos.patches.length; i++) {
    const p = atmos.patches[i]
    const o = i < layout.length ? layout[i] : undefined
    if (o === undefined || radius <= 0) p.set(0, 0, 0, 0)
    else p.set(cx + o.x * radius, cy + o.y * radius, cz + o.z * radius, o.w * radius)
  }
}

const _hue = new Color()

/**
 * The dust light tint: the mean of the system's nebula hues (linear), scaled so its brightest channel is 1 (a hue,
 * not a brightness), mixed from white by `amount`. Written into `out`.
 */
export function dustTintFor(hues: readonly string[], amount: number, out: Color): Color {
  out.setRGB(0, 0, 0)
  for (const h of hues) out.add(_hue.set(h))
  const peak = Math.max(out.r, out.g, out.b, 1e-6)
  out.multiplyScalar(1 / peak)
  return out.setRGB(1 + (out.r - 1) * amount, 1 + (out.g - 1) * amount, 1 + (out.b - 1) * amount)
}
